"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { StatCard } from "@/components/ui/StatCard";
import { Card, CardHeader, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { SeedDataPanel } from "@/components/dashboard/SeedDataPanel";
import { formatCurrency } from "@/lib/utils";

interface Opportunity {
  id: string;
  kind: string;
  title: string;
  subtitle: string;
  customerId?: string;
  customerName?: string;
  dueInDays?: number;
  suggestedAction: string;
  estimatedValue?: number;
  confidence?: number;
}

interface Layaway {
  id: string;
  status: string;
  remainingBalance: number;
  customer: { firstName: string | null; lastName: string | null };
}

interface Repair {
  id: string;
  status: string;
  customer: { firstName: string | null; lastName: string | null };
}

interface JewelryItem {
  id: string;
  name: string;
  sku: string;
  retailPrice: number;
  photoUrls: string[];
  status: string;
}

interface DashboardStats {
  todayRevenue: number;
  piecesSold: number;
  activeLayaways: number;
  openRepairs: number;
  inventoryValue: number;
  readyForPickup: number;
}

const KIND_LABEL: Record<string, { label: string; variant: "info" | "success" | "warning" | "danger" | "default" }> = {
  ANNIVERSARY: { label: "Anniversary", variant: "info" },
  BIRTHDAY: { label: "Birthday", variant: "info" },
  WISHLIST_MATCH: { label: "Wishlist match", variant: "success" },
  REPAIR_READY: { label: "Repair ready", variant: "success" },
  LAYAWAY_DUE: { label: "Layaway due", variant: "warning" },
  MEMO_DUE: { label: "Memo due", variant: "warning" },
  AGED_INVENTORY: { label: "Aged inventory", variant: "default" },
};

export default function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats>({
    todayRevenue: 0, piecesSold: 0, activeLayaways: 0, openRepairs: 0, inventoryValue: 0, readyForPickup: 0,
  });
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [featured, setFeatured] = useState<JewelryItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();

      const [oppsRes, layawayRes, repairRes, invRes, ordersRes] = await Promise.all([
        fetch("/api/opportunities"),
        fetch("/api/layaways"),
        fetch("/api/repairs"),
        fetch("/api/jewelry-items?limit=6&status=IN_STOCK"),
        fetch(`/api/orders?from=${startOfDay}&limit=200`),
      ]);

      // Opportunities
      if (oppsRes.ok) {
        const data = await oppsRes.json();
        setOpportunities((data.data || []).slice(0, 6));
      }

      // Layaways
      let activeLayaways = 0;
      if (layawayRes.ok) {
        const data = await layawayRes.json();
        const layaways: Layaway[] = data.data || [];
        activeLayaways = layaways.filter((l) => l.status === "ACTIVE").length;
      }

      // Repairs
      let openRepairs = 0;
      let readyForPickup = 0;
      if (repairRes.ok) {
        const data = await repairRes.json();
        const repairs: Repair[] = data.data || [];
        openRepairs = repairs.filter((r) => !["PICKED_UP", "CANCELLED"].includes(r.status)).length;
        readyForPickup = repairs.filter((r) => r.status === "READY_FOR_PICKUP").length;
      }

      // Featured inventory
      let inventoryValue = 0;
      if (invRes.ok) {
        const data = await invRes.json();
        const items: JewelryItem[] = data.data || [];
        setFeatured(items.slice(0, 4));
        inventoryValue = items.reduce((sum, i) => sum + (i.retailPrice || 0), 0);
      }

      // Today's revenue + pieces sold
      let todayRevenue = 0;
      let piecesSold = 0;
      if (ordersRes.ok) {
        const data = await ordersRes.json();
        const orders = data.data || [];
        todayRevenue = orders.reduce((sum: number, o: { totalAmount?: number }) => sum + (o.totalAmount || 0), 0);
        piecesSold = orders.reduce((sum: number, o: { _count?: { items: number } }) => sum + (o._count?.items || 0), 0);
      }

      setStats({ todayRevenue, piecesSold, activeLayaways, openRepairs, inventoryValue, readyForPickup });
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 60000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-semibold text-white">Good morning</h1>
        <p className="mt-1 text-muted">
          Here&apos;s what your boutique needs from you today.
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          title="Today&apos;s revenue"
          value={formatCurrency(stats.todayRevenue)}
          change={stats.piecesSold > 0 ? `${stats.piecesSold} piece${stats.piecesSold === 1 ? "" : "s"} sold` : "No sales yet"}
          changeType={stats.piecesSold > 0 ? "up" : "neutral"}
          className="animate-slide-up stagger-1"
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatCard
          title="Active layaways"
          value={String(stats.activeLayaways)}
          change="In-store installments"
          changeType="neutral"
          className="animate-slide-up stagger-2"
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatCard
          title="Open repairs"
          value={String(stats.openRepairs)}
          change={stats.readyForPickup > 0 ? `${stats.readyForPickup} ready to pick up` : "None ready today"}
          changeType={stats.readyForPickup > 0 ? "up" : "neutral"}
          className="animate-slide-up stagger-3"
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
        />
        <StatCard
          title="Opportunities"
          value={String(opportunities.length)}
          change={opportunities.length > 0 ? "AI surfaced" : "None queued"}
          changeType={opportunities.length > 0 ? "up" : "neutral"}
          className="animate-slide-up stagger-4"
          icon={
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
        />
      </div>

      {/* Opportunities */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-white">Today&apos;s opportunities</h2>
              <p className="text-xs text-muted mt-0.5">AI-ranked so you start with the highest-impact client action.</p>
            </div>
            <Link href="/dashboard/opportunities" className="text-xs text-amber-200/80 hover:text-amber-200">
              View all →
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-white" />
            </div>
          ) : opportunities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <svg className="mb-3 h-10 w-10 text-surface-border-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <p className="text-sm text-dim">No opportunities yet</p>
              <p className="mt-1 text-xs text-dim">
                Load demo data to see AI-ranked clienteling suggestions.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {opportunities.map((o) => {
                const meta = KIND_LABEL[o.kind] || { label: o.kind, variant: "default" as const };
                return (
                  <div
                    key={o.id}
                    className="flex items-start justify-between gap-4 rounded-xl border border-surface-border bg-white/[0.03] p-4 transition-colors hover:bg-white/5"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={meta.variant} size="sm">{meta.label}</Badge>
                        {typeof o.dueInDays === "number" && (
                          <span className="text-[10px] uppercase tracking-wide text-dim">
                            {o.dueInDays === 0 ? "Today" : `${o.dueInDays}d`}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-white truncate">{o.title}</p>
                      <p className="text-xs text-muted mt-0.5 line-clamp-2">{o.suggestedAction}</p>
                    </div>
                    {o.estimatedValue ? (
                      <div className="text-right shrink-0">
                        <p className="text-xs text-dim">Value</p>
                        <p className="text-sm font-medium text-amber-200">{formatCurrency(o.estimatedValue)}</p>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Featured inventory */}
      {featured.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">On the floor</h2>
              <Link href="/dashboard/inventory" className="text-xs text-amber-200/80 hover:text-amber-200">
                Browse inventory →
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {featured.map((item) => (
                <Link key={item.id} href={`/dashboard/inventory/${item.id}`} className="group">
                  <div className="aspect-square overflow-hidden rounded-xl border border-surface-border bg-white/[0.03]">
                    {item.photoUrls && item.photoUrls[0] ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={item.photoUrls[0]}
                        alt={item.name}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-dim text-xs">No photo</div>
                    )}
                  </div>
                  <div className="mt-2">
                    <p className="text-xs text-muted truncate">{item.sku}</p>
                    <p className="text-sm text-white truncate">{item.name}</p>
                    <p className="text-sm text-amber-200 mt-0.5">{formatCurrency(item.retailPrice)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <SeedDataPanel />

      {/* Quick Actions */}
      <div>
        <h2 className="mb-3 text-lg font-semibold text-white">Quick actions</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: "New sale", icon: "M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z", href: "/dashboard/pos" },
            { label: "Clients", icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M15 7a3 3 0 11-6 0 3 3 0 016 0z", href: "/dashboard/customers" },
            { label: "Wishlists", icon: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z", href: "/dashboard/wishlists" },
            { label: "Appointments", icon: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z", href: "/dashboard/appointments" },
          ].map((action) => (
            <Link
              key={action.label}
              href={action.href}
              className="flex flex-col items-center gap-2 rounded-xl border border-surface-border bg-white/5 p-4 text-muted transition-all duration-200 hover:border-surface-border-2 hover:bg-white/10 hover:text-white"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d={action.icon} />
              </svg>
              <span className="text-xs font-medium">{action.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
